import React, { useMemo, useState } from 'react';
import { addDays, minutesFromTime, startOfWeek, toDateKey } from '../utils/planningDates';
import { getAllDayEventsForDate, getNowMinutes, isToday, layoutEventsForDate } from '../utils/planningLayout';

const PIXELS_PER_MINUTE = 0.75;
export const TIMELINE_START_MINUTES = 7 * 60;
export const TIMELINE_END_MINUTES = 20 * 60;

function TimelineEvent({ event, onOpen, startMinutes, endMinutes }) {
  const visibleStart = Math.max(event.startMinutes, startMinutes);
  const visibleEnd = Math.min(event.endMinutes, endMinutes);
  if (visibleEnd <= visibleStart) return null;
  return (
    <button
      className={`planning-event planning-event--${event.elementType} ${event.syncStatus !== 'synced' ? 'planning-event--unsynced' : ''}`}
      style={{
        top: (visibleStart - startMinutes) * PIXELS_PER_MINUTE,
        height: Math.max(22, (visibleEnd - visibleStart) * PIXELS_PER_MINUTE),
        left: `calc(${event.leftPercent}% + 2px)`,
        width: `calc(${event.widthPercent}% - 4px)`,
      }}
      onClick={() => onOpen(event.id)}
      onDoubleClick={(mouseEvent) => mouseEvent.stopPropagation()}
      aria-label={`${event.title}, ${event.startTime || 'journée entière'}`}
    >
      <strong>{event.title || 'Sans titre'}</strong>
      <span>{event.startTime}{event.owner ? ` · ${event.owner}` : ''}</span>
    </button>
  );
}

function TimeScale({ startMinutes, endMinutes }) {
  const height = (endMinutes - startMinutes) * PIXELS_PER_MINUTE;
  const firstHour = Math.ceil(startMinutes / 60);
  const hours = Array.from({ length: Math.floor(endMinutes / 60) - firstHour + 1 }, (_, index) => firstHour + index);
  return <div className="planning-timeline__scale" style={{ height }}>{hours.map((hour) => <time key={hour} style={{ top: (hour * 60 - startMinutes) * PIXELS_PER_MINUTE }}>{String(hour).padStart(2, '0')}:00</time>)}</div>;
}

function DayColumn({ date, events, onOpen, onCreateAt, startMinutes, endMinutes, now = new Date() }) {
  const dateKey = toDateKey(date);
  const positioned = useMemo(() => layoutEventsForDate(events, dateKey), [events, dateKey]);
  const height = (endMinutes - startMinutes) * PIXELS_PER_MINUTE;
  const handleDoubleClick = (clickEvent) => {
    const bounds = clickEvent.currentTarget.getBoundingClientRect();
    const minutes = Math.round((startMinutes + (clickEvent.clientY - bounds.top) / PIXELS_PER_MINUTE) / 15) * 15;
    onCreateAt(dateKey, Math.max(startMinutes, Math.min(endMinutes - 1, minutes)));
  };
  const hours = Array.from({ length: Math.floor((endMinutes - startMinutes) / 60) + 1 }, (_, index) => startMinutes + index * 60);
  const nowMinutes = getNowMinutes(now);
  return (
    <div className={`planning-timeline__day ${[0, 6].includes(date.getDay()) ? 'is-weekend' : ''} ${isToday(date, now) ? 'is-today' : ''}`} style={{ height }} onDoubleClick={handleDoubleClick} title="Double-cliquez pour créer un élément">
      {hours.map((minutes) => <span className="planning-timeline__hour-line" style={{ top: (minutes - startMinutes) * PIXELS_PER_MINUTE }} key={minutes}/>)}
      {isToday(date, now) && nowMinutes >= startMinutes && nowMinutes <= endMinutes && <span className="planning-timeline__now" style={{ top: (nowMinutes - startMinutes) * PIXELS_PER_MINUTE }} aria-label="Heure actuelle"/>}
      {positioned.map((event) => <TimelineEvent key={`${event.id}-${dateKey}`} event={event} onOpen={onOpen} startMinutes={startMinutes} endMinutes={endMinutes}/>)}
    </div>
  );
}

function AllDayRow({ dates, events, onOpen }) {
  return <div className="planning-timeline__all-day"><strong>Journée</strong>{dates.map((date) => { const dateKey = toDateKey(date); return <div key={dateKey}>{getAllDayEventsForDate(events, dateKey).map((event) => <button className={`planning-event planning-event--${event.elementType}`} key={event.id} onClick={() => onOpen(event.id)}>{event.title}</button>)}</div>; })}</div>;
}

function Timeline({ dates, events, onOpen, onCreateAt, day = false }) {
  const [fullDay, setFullDay] = useState(false);
  const startMinutes = fullDay ? 0 : TIMELINE_START_MINUTES;
  const endMinutes = fullDay ? 1440 : TIMELINE_END_MINUTES;
  const outsideCount = events.filter((event) => !event.isAllDay && (minutesFromTime(event.startTime) < TIMELINE_START_MINUTES || minutesFromTime(event.endTime) > TIMELINE_END_MINUTES)).length;
  return (
    <div className={`planning-timeline ${day ? 'planning-timeline--day' : 'planning-timeline--week'}`}>
      <div className="planning-timeline__headers"><span/><>{dates.map((date) => <strong className={isToday(date) ? 'is-today' : ''} key={toDateKey(date)}>{date.toLocaleDateString('fr-FR', day ? { weekday: 'long', day: 'numeric', month: 'long' } : { weekday: 'short', day: 'numeric' })}</strong>)}</></div>
      <AllDayRow dates={dates} events={events} onOpen={onOpen}/>
      <div className="planning-timeline__range-toggle"><button type="button" onClick={() => setFullDay((value) => !value)}>{fullDay ? 'Afficher 07:00–20:00' : `Afficher 24 h${outsideCount ? ` · ${outsideCount} hors plage` : ''}`}</button></div>
      <div className="planning-timeline__scroll">
        <TimeScale startMinutes={startMinutes} endMinutes={endMinutes}/>
        {dates.map((date) => <DayColumn key={toDateKey(date)} date={date} events={events} onOpen={onOpen} onCreateAt={onCreateAt} startMinutes={startMinutes} endMinutes={endMinutes}/>)}
      </div>
    </div>
  );
}

export function PlanningWeekTimeline(props) {
  const dates = useMemo(() => Array.from({ length: 7 }, (_, index) => addDays(startOfWeek(props.anchor), index)), [props.anchor]);
  return <Timeline {...props} dates={dates}/>;
}

export function PlanningDayTimeline(props) {
  return <Timeline {...props} dates={[props.anchor]} day/>;
}
